import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    ElementRef,
} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {FormArray, FormBuilder} from '@angular/forms';
import {AppearanceEditor} from '@common/admin/appearance/appearance-editor/appearance-editor.service';
import {Settings} from '@common/core/config/settings.service';
import {map} from 'rxjs/operators';
import * as Dot from 'dot-object';
import {HomepageContent} from '../../../landing/homepage-content';
import {RIGHT_POSITION} from '@common/core/ui/overlay-panel/positions/right-position';
import {SelectMenuItemActionComponent} from '@common/admin/appearance/panels/menus-appearance-panel/menus/add-menu-item-panel/select-menu-item-action.component';
import {MenuItem} from '@common/core/ui/custom-menu/menu-item';
import {OverlayPanel} from '@common/core/ui/overlay-panel/overlay-panel.service';

const CONFIG_KEY = 'homepage.appearance';

@Component({
    selector: 'homepage-appearance-panel',
    templateUrl: './homepage-appearance-panel.component.html',
    styleUrls: ['./homepage-appearance-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomepageAppearancePanelComponent implements OnInit {
    public selectedSubpanel$ = new BehaviorSubject<string>(null);
    public defaultValues: HomepageContent;

    public path$ = this.selectedSubpanel$.pipe(
        map(panel => {
            const path = ['Homepage'];
            if (panel) path.push(panel);
            return path;
        })
    );

    public form = this.fb.group({
        headerTitle: [''],
        headerSubtitle: [''],
        headerImage: [''],
        headerImageOpacity: [1],
        headerOverlayColor1: [''],
        headerOverlayColor2: [''],
        footerTitle: [''],
        footerSubtitle: [''],
        footerImage: [''],
        actions: this.fb.group({
            inputText: [''],
            inputButton: [''],
            cta1: this.fb.group({
                label: '',
                action: '',
                type: '',
                model_id: '',
            }),
            cta2: this.fb.group({
                label: '',
                action: '',
                type: '',
                model_id: '',
            }),
        }),
        primaryFeatures: this.fb.array([]),
        secondaryFeatures: this.fb.array([]),
    });

    callToActions: {label: string; key: string; highlight: string}[] = [
        {
            label: 'Call to Action #1',
            key: 'cta1',
            highlight: '.get-started-button',
        },
        {
            label: 'Call to Action #2',
            key: 'cta2',
            highlight: '.learn-more-button',
        },
    ];

    constructor(
        private fb: FormBuilder,
        private editor: AppearanceEditor,
        private settings: Settings,
        private panel: OverlayPanel,
        private el: ElementRef
    ) {}

    ngOnInit() {
        const data = this.settings.getJson(CONFIG_KEY) as HomepageContent;
        this.defaultValues = JSON.parse(
            this.editor.defaultSettings[CONFIG_KEY]
        );

        data.primaryFeatures.forEach(() => {
            this.addFeature('primary');
        });
        data.secondaryFeatures.forEach(() => {
            this.addFeature('secondary');
        });

        this.form.patchValue(data);

        this.form.valueChanges.subscribe(value => {
            this.editor.setConfig(CONFIG_KEY, value);
            this.editor.addChanges({[CONFIG_KEY]: value});
        });
    }

    public openPreviousPanel() {
        if (this.selectedSubpanel$.value) {
            this.openSubpanel(null);
        } else {
            this.editor.closeActivePanel();
        }
    }

    public openSubpanel(name: string) {
        this.selectedSubpanel$.next(name);
    }

    public addFeature(type: 'primary' | 'secondary') {
        const features = this.form.get(`${type}Features`) as FormArray;
        const data: {[key: string]: string[]} = {
            title: [''],
            subtitle: [''],
            image: [''],
        };
        if (type === 'secondary') {
            data.description = [''];
        }
        features.push(this.fb.group(data));
    }

    public removeFeature(type: 'primary' | 'secondary', index: number) {
        const features = this.form.get(`${type}Features`) as FormArray;
        features.removeAt(index);
    }

    public defaultValue(key: string): string {
        return Dot.pick(key, this.defaultValues) || '';
    }

    public primaryArray() {
        return this.form.get('primaryFeatures') as FormArray;
    }

    public secondaryArray() {
        return this.form.get('secondaryFeatures') as FormArray;
    }

    openSelectMenuItemActionDialog(formPath: string) {
        const position = RIGHT_POSITION.slice();
        this.panel
            .open(SelectMenuItemActionComponent, {
                position,
                origin: this.el,
                panelClass: 'add-menu-item-panel-container',
            })
            .afterClosed()
            .subscribe((val: Partial<MenuItem>) => {
                if (val) {
                    // don't override label
                    delete val.label;
                    this.form.get(formPath).patchValue(val);
                }
            });
    }
}
